import React, { useEffect, useState } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from 'src/app/store/store';
import { AuthProvider } from 'src/lib/FirebaseContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'src/components/ui/Toaster';
import CookieConsentDialog from 'src/components/CookieConsentDialog';
import ApiErrorDialog from 'src/components/errorDialog';
import { useAppSelector } from 'src/app/store/hooks';
import { usePostHog, cloudPosthog } from 'src/lib/posthog';
import { getGroupIdFromPath, getTenantFromPath } from 'src/lib/tenantRouting';
import useAuth from 'src/hooks/useAuth';
import { bindCanvasDocumentViewer, canvasViewerKey } from 'src/features/chat/api';

if (import.meta.env.MODE === 'development') {
  // Support posthog toolbar
  (window as any).posthog = cloudPosthog;
}

// Custom retry function that doesn't retry 403/404 errors (access denied, not found)
// These are permanent failures that retrying won't fix
const shouldRetryQuery = (failureCount: number, error: unknown) => {
  // Default max retries is 3
  if (failureCount >= 3) return false;

  // Check for axios-style error response
  const axiosError = error as { response?: { status?: number } };
  const status = axiosError?.response?.status;

  // Don't retry 403 (access denied) or 404 (not found) - these are permanent failures
  if (status === 403 || status === 404) return false;

  // Retry other errors
  return true;
};

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery
    }
  }
});

// Component that needs Redux access
const AppProviders = ({ children }: { children: React.ReactNode }) => {
  const globalError = useAppSelector((state: any) => state.notifications.globalError);
  const [cookieConsentOpen, setCookieConsentOpen] = useState<boolean | null>(null);
  const { pathname, search } = useLocation();
  const posthog = usePostHog();
  const { user } = useAuth();

  useEffect(() => {
    bindCanvasDocumentViewer(canvasViewerKey(user));
  }, [user]);

  useEffect(() => {
    if (document.cookie.includes('cookie_consent=true')) {
      setCookieConsentOpen(false);
    } else {
      setCookieConsentOpen(true);
    }
  }, []);

  // While the user is inside a group (hub tabs such as dashboards, training,
  // and actions, or an action review opened from that group), tag every
  // event with the group so activity can be compared across those features.
  useEffect(() => {
    const groupId = getGroupIdFromPath(pathname) || new URLSearchParams(search).get('group');
    if (groupId) {
      posthog.register({ group_id: groupId });
    } else {
      posthog.unregister('group_id');
    }
  }, [pathname, search, posthog]);

  useEffect(() => {
    const tenant = getTenantFromPath(pathname);
    if (tenant) {
      posthog.register({ tenant });
    } else {
      posthog.unregister('tenant');
    }
  }, [pathname, posthog]);

  useEffect(() => {
    posthog.capture('$pageview', {
      path: pathname
    });
  }, [pathname]);

  const handleCookieConsentAccept = () => {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 150);
    document.cookie = `cookie_consent=true; expires=${expirationDate.toUTCString()};`;
    setCookieConsentOpen(false);
  };

  return (
    <>
      <ApiErrorDialog error={globalError?.error} actions={globalError?.actions} />
      <CookieConsentDialog
        open={cookieConsentOpen === true}
        onClose={() => setCookieConsentOpen(false)}
        onAccept={handleCookieConsentAccept}
      />
      <Toaster>{children}</Toaster>
    </>
  );
};

// ----------------------------------------------------------------------

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider = ({ children }: AppProviderProps) => {
  return (
    <HelmetProvider>
      <ReduxProvider store={store}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <AppProviders>{children}</AppProviders>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ReduxProvider>
    </HelmetProvider>
  );
};
